import type { MediaPort, UploadPurpose } from './ports.ts';
import { WorkerRequestError, type WorkerClient } from './worker.ts';

type UploadIntent = { uploadUrl: string; parameters: Record<string, string | number> };

// Signed upload: the Worker issues the parameters, the file goes straight to
// Cloudinary (or the local fake media server in development).
export function createMedia(worker: WorkerClient, fetchImpl: typeof fetch = (...args) => fetch(...args)): MediaPort {
  async function send(file: Blob, purpose: string, shopId: string | undefined, what: string) {
    const intent = await worker.call<UploadIntent>('create_media_upload_intent', {
      purpose, contentType: file.type, bytes: file.size, ...(shopId ? { shopId } : {}),
    });
    const form = new FormData();
    for (const [key, value] of Object.entries(intent.parameters)) form.append(key, String(value));
    form.append('file', file);
    let response: Response;
    try {
      response = await fetchImpl(intent.uploadUrl, { method: 'POST', body: form });
    } catch {
      throw new WorkerRequestError('upload_failed', `The ${what} could not be uploaded. Check your connection and try again.`, 0);
    }
    const result = await response.json().catch(() => ({})) as { secure_url?: string; public_id?: string };
    if (!response.ok || !result.secure_url) throw new WorkerRequestError('upload_failed', `The ${what} could not be uploaded. Try a different ${what}.`, response.status);
    return { url: result.secure_url, publicId: String(result.public_id || '') };
  }
  // A chat video goes up with XMLHttpRequest, which reports progress and can be cancelled.
  async function sendWithProgress(file: Blob, onProgress?: (fraction: number) => void, signal?: AbortSignal) {
    const intent = await worker.call<UploadIntent>('create_media_upload_intent', { purpose: 'message-video', contentType: file.type, bytes: file.size });
    if (signal?.aborted) throw new WorkerRequestError('upload_cancelled', 'Upload cancelled.', 0);
    const form = new FormData();
    for (const [key, value] of Object.entries(intent.parameters)) form.append(key, String(value));
    form.append('file', file);
    return new Promise<{ url: string; publicId: string }>((resolve, reject) => {
      const request = new XMLHttpRequest();
      const abort = () => request.abort();
      signal?.addEventListener('abort', abort, { once: true });
      request.upload.onprogress = event => { if (event.lengthComputable) onProgress?.(event.loaded / event.total); };
      request.onload = () => {
        signal?.removeEventListener('abort', abort);
        let result: { secure_url?: string; public_id?: string } = {};
        try { result = JSON.parse(request.responseText) as typeof result; } catch { /* Not JSON. */ }
        if (request.status >= 200 && request.status < 300 && result.secure_url) resolve({ url: result.secure_url, publicId: String(result.public_id || '') });
        else reject(new WorkerRequestError('upload_failed', 'The video could not be uploaded. Try a different video.', request.status));
      };
      request.onerror = () => { signal?.removeEventListener('abort', abort); reject(new WorkerRequestError('upload_failed', 'The video could not be uploaded. Check your connection and try again.', 0)); };
      request.onabort = () => reject(new WorkerRequestError('upload_cancelled', 'Upload cancelled.', 0));
      request.open('POST', intent.uploadUrl);
      request.send(form);
    });
  }
  return {
    async upload(file: Blob, purpose: UploadPurpose, options = {}) {
      return (await send(file, purpose, options.shopId, 'photo')).url;
    },
    uploadVideo: (file, options) => send(file, options.for === 'listing' ? 'product-video' : 'post-video', options.shopId, 'video'),
    uploadChatVideo: (file, options = {}) => sendWithProgress(file, options.onProgress, options.signal),
  };
}
